import {
  AddPageButton,
  AppSidebar,
  CategoryDivider,
  MenuItem,
  MenuLinkItem,
  QuickSearchInput,
  SidebarContainer,
  SidebarScrollableContainer,
} from '@affine/core/modules/app-sidebar/views';
import { GlobalDialogService, WorkspaceDialogService, } from '@affine/core/modules/dialogs';
import { WorkbenchLink } from '@affine/core/modules/workbench';
import { ExplorerFavorites, ExplorerMigrationFavorites, } from '@affine/core/modules/explorer';
import { ExplorerTags } from '@affine/core/modules/explorer/views/sections/tags';
import { CMDKQuickSearchService } from '@affine/core/modules/quicksearch/services/cmdk';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import { AllDocsIcon, BulletedListIcon, DatabaseKanbanViewIcon, ImportIcon, SettingsIcon, GroupIcon, MobileHomeIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService, useServices } from '@toeverything/infra';
import type { ReactElement } from 'react';
import { memo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom'

import { WorkbenchService } from '../../modules/workbench';
import { WorkspaceNavigator } from '../workspace-selector';
import {
  quickSearch,
  quickSearchAndNewPage,
  saveAndOutBtn,
  workspaceAndUserWrapper,
  workspaceWrapper
} from './index.css';
import { TrashButton } from './trash-button';
import { UserInfo } from './user-info';
import { Button, Tooltip } from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { AppSidebarService } from "@affine/core/modules/app-sidebar";

// export type RootAppSidebarProps = {
//   isPublicWorkspace: boolean;
//   onOpenQuickSearchModal: () => void;
//   onOpenSettingModal: () => void;
//   currentWorkspace: Workspace;
//   openPage: (pageId: string) => void;
//   createPage: () => Doc;
//   paths: {
//     all: (workspaceId: string) => string;
//     trash: (workspaceId: string) => string;
//     shared: (workspaceId: string) => string;
//   };
// };

export const TipIconText = ({ text }) => {
  return <Tooltip placement="right" title={text}>
    <Button
      style={{ position: 'absolute', right: '5px', top: '50%', transform: 'translateY(-50%)' }}
      icon={<QuestionCircleOutlined />}
      type={'text'}
      onClick={e => {
        e.preventDefault();
        e.stopPropagation()
      }}
    />
  </Tooltip>
}

export const AllDocsButton = () => {
  const t = useI18n();
  const { workbenchService } = useServices({
    WorkbenchService,
  });
  const workbench = workbenchService.workbench;
  const allPageActive = useLiveData(
    workbench.location$.selector(location => location.pathname === '/all')
  );

  return (
    <MenuLinkItem icon={ <AllDocsIcon /> } active={ allPageActive } to={ '/all' }>
      <span data-testid="all-pages">
        文档库
      </span>
      <TipIconText text={'在线编辑，文档托管'} />
    </MenuLinkItem>
  );
};

export const KnowledgeButton = () => {
  const { workbenchService } = useServices({
    WorkbenchService,
  });
  const workbench = workbenchService.workbench;
  const allPageActive = useLiveData(
    workbench.location$.selector(location => location.pathname === '/knowledge-base')
  );

  const appSidebarService = useService(AppSidebarService).sidebar;
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const open = useLiveData(appSidebarService.open$);
  const handleClickSwitch = useCallback(() => {
    if (!open) return
    track.$.navigationPanel.$.toggle({
      type: open ? 'collapse' : 'expand',
    });
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (open) {
      appSidebarService.setHovering(false);
      timeoutRef.current = setTimeout(() => {
        appSidebarService.setPreventHovering(false);
      }, 500);
    }

    appSidebarService.setPreventHovering(true);
    appSidebarService.toggleSidebar();
  }, [appSidebarService, open]);

  return (
    <MenuLinkItem icon={ <DatabaseKanbanViewIcon /> } active={ allPageActive } to={ '/knowledge-base' } onClick={handleClickSwitch}>
      <span data-testid="knowledge-base">
        知识库
      </span>
      <TipIconText text={'上传文档，通过AI检索生成相关内容'} />
    </MenuLinkItem>
  );
};

export const GroupButton = () => {
  const { workbenchService } = useServices({
    WorkbenchService,
  });
  const workbench = workbenchService.workbench;
  const pageActive = useLiveData(
    workbench.location$.selector(location => location.pathname === '/group')
  );

  return (
    <MenuLinkItem icon={ <GroupIcon /> } active={ pageActive } to={ '/group' }>
      <span data-testid="group">
        专家组
      </span>
      <TipIconText text={'招募专家，在线沟通'} />
    </MenuLinkItem>
  );
}

// 首页菜单
export const HomeMenuButton = () => {
  const { workbenchService } = useServices({
    WorkbenchService,
  });
  const workbench = workbenchService.workbench;
  const allPageActive = useLiveData(
    workbench.location$.selector(location => location.pathname === '/home')
  );

  return (
    <MenuLinkItem icon={ <MobileHomeIcon /> } active={ allPageActive } to={ '/home' }>
      首页
    </MenuLinkItem>
  );
}

// 任务菜单
export const TaskListButton = () => {
  const t = useI18n();
  const { workbenchService } = useServices({
    WorkbenchService,
  });
  const workbench = workbenchService.workbench;
  const allPageActive = useLiveData(
    workbench.location$.selector(location => location.pathname === '/task-list')
  );

  return (
    <MenuLinkItem icon={ <BulletedListIcon /> } active={ allPageActive } to={ '/task-list' }>
        任务广场
        <TipIconText text={'发布接受查看撰写任务'} />
    </MenuLinkItem>
  );
}

export const DocsExitButton = () => {
  return <WorkbenchLink to={ '/all' }>
    <Button
      type={'text'}
      icon={<AllDocsIcon style={{ fontSize: '18px', color: '#1443ea' }} />}
      className={saveAndOutBtn}
    >
      保存离开
    </Button>
  </WorkbenchLink>
};

/**
 * This is for the whole affine app sidebar.
 * This component wraps the app sidebar in `@affine/component` with logic and data.
 *
 */
export const RootAppSidebar = memo((): ReactElement => {
  const { workbenchService, cMDKQuickSearchService } = useServices({
    WorkbenchService,
    CMDKQuickSearchService,
  });
  const navigate = useNavigate();
  const t = useI18n();
  const globalDialogService = useService(GlobalDialogService);
  const workspaceDialogService = useService(WorkspaceDialogService);
  const workbench = workbenchService.workbench;
  const onOpenQuickSearchModal = useCallback(() => {
    cMDKQuickSearchService.toggle();
  }, [ cMDKQuickSearchService ]);

  const onOpenSettingModal = useCallback(() => {
    globalDialogService.open('setting', {
      activeTab: 'appearance',
    });
    track.$.navigationPanel.$.openSettings();
  }, [ globalDialogService ]);

  const handleOpenDocs = useCallback(
    (result: {
      docIds: string[];
      entryId?: string;
      isWorkspaceFile?: boolean;
    }) => {
      const { docIds, entryId, isWorkspaceFile } = result;
      // If the imported file is a workspace file, open the entry page.
      if (isWorkspaceFile && entryId) {
        workbench.openDoc(entryId);
      } else if (!docIds.length) {
        return;
      }
      // Open all the docs when there are multiple docs imported.
      if (docIds.length > 1) {
        workbench.openAll();
      } else {
        // Otherwise, open the only doc.
        workbench.openDoc(docIds[0]);
      }
    },
    [ workbench ]
  );

  const onOpenImportModal = useCallback(() => {
    track.$.navigationPanel.importModal.open();
    workspaceDialogService.open('import', undefined, payload => {
      if (!payload) {
        return;
      }
      handleOpenDocs(payload);
    });
  }, [ workspaceDialogService, handleOpenDocs ]);

  return (
    <AppSidebar>
      <SidebarContainer>
        <div className={ workspaceAndUserWrapper }>
          <div className={ workspaceWrapper }>
            <WorkspaceNavigator
              showEnableCloudButton
              showSettingsButton
              showSyncStatus
            />
          </div>
          <UserInfo />
        </div>
        <div className={ quickSearchAndNewPage }>
          <QuickSearchInput
            className={ quickSearch }
            data-testid="slider-bar-quick-search-button"
            data-event-props="$.navigationPanel.$.quickSearch"
            onClick={ onOpenQuickSearchModal }
          />
          <AddPageButton />
        </div>
        <HomeMenuButton />
        <TaskListButton />
        <AllDocsButton />
        <KnowledgeButton />
        <GroupButton />
        {/* <AppSidebarJournalButton /> */}
        <MenuItem
          data-testid="slider-bar-workspace-setting-button"
          icon={ <SettingsIcon /> }
          onClick={ onOpenSettingModal }
        >
          <span data-testid="settings-modal-trigger">
            { t['com.affine.settingSidebar.title']() }
          </span>
        </MenuItem>
      </SidebarContainer>
      <SidebarScrollableContainer>
        <ExplorerFavorites />
        {/* <ExplorerOrganize /> */}
        <ExplorerMigrationFavorites />
        {/* <ExplorerCollections /> */}
        <ExplorerTags />
        <CategoryDivider label={ t['com.affine.rootAppSidebar.others']() } />
        <div style={ { padding: '0 8px' } }>
          <TrashButton />
          <MenuItem
            data-testid="slider-bar-import-button"
            icon={ <ImportIcon /> }
            onClick={ onOpenImportModal }
          >
            <span data-testid="import-modal-trigger">{ t['Import']() }</span>
          </MenuItem>
          {/* <MenuItem */}
          {/*   data-testid="slider-bar-import-button" */}
          {/*   icon={ <MoveLeftIcon /> } */}
          {/*   onClick={ onBackToHome } */}
          {/* > */}
          {/*   <span data-testid="import-modal-trigger">返回首页</span> */}
          {/* </MenuItem> */}
          {/* <ExternalMenuLinkItem
            href="https://www.dawoai.com"
            icon={ <JournalIcon /> }
            label={ t['com.affine.app-sidebar.learn-more']() }
          /> */}
          {/* <ExternalMenuLinkItem
            href="https://github.com/toeverything/affine"
            icon={<GithubIcon />}
            label={t['com.affine.app-sidebar.star-us']()}
          /> */ }
        </div>
      </SidebarScrollableContainer>
      {/* <SidebarContainer> */}
      {/*   { BUILD_CONFIG.isElectron ? <UpdaterButton /> : <AppDownloadButton /> } */}
      {/* </SidebarContainer> */}
    </AppSidebar>
  );
});

RootAppSidebar.displayName = 'memo(RootAppSidebar)';
